import { Button, Card, Tooltip, Typography } from '@eye-able/ui';
import { cn } from '@helpers/cn';
import {
  css as beautifyCss,
  html as beautifyHtml,
  js as beautifyJs,
} from 'js-beautify';
import { Eye, EyeOff } from 'lucide-react';
import { useState } from 'react';
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter';
import css from 'react-syntax-highlighter/dist/esm/languages/hljs/css';
import xml from 'react-syntax-highlighter/dist/esm/languages/hljs/xml';
import syntaxTheme from 'react-syntax-highlighter/dist/esm/styles/hljs/ir-black';
import javascript from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript';
import CopyToClipboard from '@components/Button/CopyToClipboard';
import DynamicHeightIFrame from '@components/DynamicHeightIFrame';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

SyntaxHighlighter.registerLanguage('xml', xml);
SyntaxHighlighter.registerLanguage('css', css);
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('html', xml); // HTML is highlighted with the xml grammar

// ---------------------------------------------------------------------------
// Code formatting
// ---------------------------------------------------------------------------

const TRIM_MIN_LENGTH = 180;
const TRIM_MAX_LENGTH = 600;

/** Collapse a long HTML string to its outer element with a `...` placeholder. */
function trimElementIfTooLong(html: string): string {
  if (html.length < TRIM_MIN_LENGTH) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const element = doc.body.firstChild;
  if (!element || element.nodeType !== Node.ELEMENT_NODE) return html;

  while (element.firstChild) element.removeChild(element.firstChild);
  element.appendChild(doc.createComment(' ... '));

  const serialized = new XMLSerializer().serializeToString(element);
  return serialized.length > TRIM_MAX_LENGTH
    ? `${serialized.slice(0, TRIM_MAX_LENGTH - 1)} ...`
    : serialized;
}

const beautifyOptions = {
  indent_size: 2,
  preserve_newlines: true,
  max_preserve_newlines: 2,
  inline: [],
};

const beautifiers: Record<string, typeof beautifyHtml> = {
  html: beautifyHtml,
  xml: beautifyHtml,
  css: beautifyCss,
  scss: beautifyCss,
  javascript: beautifyJs,
  js: beautifyJs,
  typescript: beautifyJs,
  ts: beautifyJs,
  json: beautifyJs,
};

function formatCode(code: string, language: string): string {
  try {
    return beautifiers[language]?.(code, beautifyOptions) ?? code;
  } catch {
    return code;
  }
}

// Human-friendly names for the header title; unknown languages fall back to uppercase.
const LANGUAGE_LABELS: Record<string, string> = {
  html: 'HTML',
  xml: 'XML',
  css: 'CSS',
  scss: 'SCSS',
  javascript: 'JavaScript',
  js: 'JavaScript',
  typescript: 'TypeScript',
  ts: 'TypeScript',
  json: 'JSON',
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

// Slim scrollbar with a transparent track (grey thumb), on the container and the
// inner <pre> that scrolls horizontally. Its narrow width lets the toolbar clear it.
const transparentScrollbar = cn(
  'eye:[&::-webkit-scrollbar]:size-[10px]',
  'eye:[&::-webkit-scrollbar-track]:bg-transparent',
  'eye:[&::-webkit-scrollbar-thumb]:rounded-[10px] eye:[&::-webkit-scrollbar-thumb]:bg-grey-100',
  'eye:[&_pre::-webkit-scrollbar]:size-[10px]',
  'eye:[&_pre::-webkit-scrollbar-track]:bg-transparent',
  'eye:[&_pre::-webkit-scrollbar-thumb]:rounded-[10px] eye:[&_pre::-webkit-scrollbar-thumb]:bg-grey-100',
);

const codeCard = 'eye:overflow-hidden eye:rounded-md eye:bg-black';

const codeHeader =
  'eye:flex eye:items-center eye:justify-between eye:bg-white/10 eye:px-2xs eye:py-4xs ';

const codeScroll = cn(
  'eye:max-h-[256px] eye:overflow-x-hidden eye:overflow-y-auto eye:px-4xs',
  'eye:border-2 eye:border-black',
  'eye:outline-white eye:focus-visible:outline-2 eye:focus-visible:[outline-offset:-12px]',
  transparentScrollbar,
);

const toolbarButton =
  'eye:text-white eye:hover:bg-white/15 eye:hover:text-white eye:outline-white';

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

interface CodeToolbarProps {
  /** Text handed to the copy button. */
  code: string;
  /** Language name shown as the header title. */
  languageLabel: string;
  /** Whether to render the preview toggle alongside the copy button. */
  showPreviewToggle: boolean;
  isPreviewOpen: boolean;
  onTogglePreview: () => void;
}

/** Static header row: language title on the left, copy + preview actions on the right. */
function CodeToolbar({
  code,
  languageLabel,
  showPreviewToggle,
  isPreviewOpen,
  onTogglePreview,
}: CodeToolbarProps) {
  const { t } = useInlineTranslation();
  const toggleLabel = isPreviewOpen
    ? t('hideCodePreview')
    : t('showCodePreview');

  return (
    <div className={codeHeader}>
      <Typography tag='span' variant='body-m' className='eye:text-white!'>
        {languageLabel}
      </Typography>

      <div className='eye:flex eye:items-center eye:gap-4xs'>
        {showPreviewToggle && (
          <Tooltip content={toggleLabel}>
            <Button
              type='button'
              size='icon-xs'
              variant='ghost'
              className={toolbarButton}
              aria-label={toggleLabel}
              aria-pressed={isPreviewOpen}
              onClick={onTogglePreview}
            >
              {isPreviewOpen ? <EyeOff aria-hidden /> : <Eye aria-hidden />}
            </Button>
          </Tooltip>
        )}
        <CopyToClipboard text={code} size='icon-xs' className={toolbarButton} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CodeBlock
// ---------------------------------------------------------------------------

export interface CodeBlockProps {
  /** Code to render and copy. */
  children: string;
  /** Syntax highlighting language and header title; defaults to `html`. */
  language?: string;
  /** Collapse long HTML to its outer element before rendering. */
  trim?: boolean;
  /** Wrap long lines instead of scrolling the code horizontally (default `true`). */
  wrapLongLines?: boolean;
  /** Show a line-number gutter. */
  showLineNumbers?: boolean;
  /** Render a toggle that previews the code in a sandboxed iframe. */
  enablePreview?: boolean;
  /** Accessible label for the code region and preview iframe. */
  label?: string;
  /** Tailwind classes merged onto the scrollable code container. */
  className?: string;
  /** Remaining props are forwarded to SyntaxHighlighter (e.g. `PreTag`, `lineProps`). */
  [prop: string]: unknown;
}

function CodeBlock({
  children,
  language = 'html',
  trim = false,
  wrapLongLines = true,
  enablePreview = false,
  label,
  className,
  ...syntaxHighlighterProps
}: CodeBlockProps) {
  const { t } = useInlineTranslation();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const code = formatCode(
    trim ? trimElementIfTooLong(children) : children,
    language,
  );
  const languageLabel = LANGUAGE_LABELS[language] ?? language.toUpperCase();
  const regionLabel = label || t('codePreview');

  // Built fresh every render on purpose: react-syntax-highlighter MUTATES this object
  // (it writes `white-space` onto `.style`), so a shared instance would leak wrap vs.
  // no-wrap state between code blocks. `overflow-wrap` is inherited by the token spans,
  // so long unbreakable tokens (e.g. URLs) wrap instead of widening and shifting the
  // layout, while normal code still breaks at its natural boundaries.
  const codeTagProps = { style: { overflowWrap: 'anywhere' as const } };

  return (
    <div className='eye:w-full'>
      <div className={codeCard}>
        <CodeToolbar
          code={code}
          languageLabel={languageLabel}
          showPreviewToggle={enablePreview}
          isPreviewOpen={isPreviewOpen}
          onTogglePreview={() => setIsPreviewOpen((open) => !open)}
        />
        <div
          role='group'
          aria-label={regionLabel}
          tabIndex={0}
          className={cn(codeScroll, className)}
        >
          <SyntaxHighlighter
            language={language}
            style={syntaxTheme}
            codeTagProps={codeTagProps}
            wrapLongLines={wrapLongLines}
            wrapLines
            {...syntaxHighlighterProps}
          >
            {code}
          </SyntaxHighlighter>
        </div>
      </div>

      {enablePreview && isPreviewOpen && (
        <Card
          role='region'
          aria-label={regionLabel}
          className='eye:mt-3xs eye:overflow-hidden'
        >
          <DynamicHeightIFrame title={regionLabel}>
            {children}
          </DynamicHeightIFrame>
        </Card>
      )}
    </div>
  );
}

export default CodeBlock;
